import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Badge, Button } from "@/components/ui/PageHeader";
import { InputField, TextAreaField, SelectField } from "@/components/ui/FormFields";
import { Modal } from "@/components/ui/Modal";
import { useAuth } from "@/context/AuthContext";
import {
  listVoices,
  listVoicesByType,
  getCommunityPulse,
  createVoice,
  VOICE_TYPES,
  VOICE_CATEGORIES,
  DECLARED_ORIGINS,
  type CreateVoiceInput,
} from "@/api/voices";
import { uploadImage, uploadCameraImage } from "@/api/upload";

export default function VoicesLab() {
  const qc = useQueryClient();
  const { isAuthenticated, user } = useAuth();
  const [addOpen, setAddOpen] = useState(false);
  // GET /voices?userId= — the member's own Awaaz list.
  const [mineOnly, setMineOnly] = useState(false);
  const mine = mineOnly && user ? user.id : undefined;

  const { data: all, isLoading } = useQuery({
    queryKey: ["voices-all", mine ?? "everyone"],
    queryFn: () => listVoices({ limit: 30, userId: mine }),
  });

  return (
    <div className="space-y-8">
      <PageHeader
        title="Voices (Awaaz)"
        subtitle="Community advocacy + discussions. Browse recent voices."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Voices" },
        ]}
        actions={
          isAuthenticated && (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              Raise a voice
            </Button>
          )
        }
      />

      <AddVoiceModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setAddOpen(false);
          qc.invalidateQueries({ queryKey: ["voices-all"] });
        }}
      />

      <CommunityPulsePanel />

      <ByType />

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">{mine ? "My voices" : "Recent"}</h2>
          {isAuthenticated && (
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
              Mine only
            </label>
          )}
        </div>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !all || all.data.voices.length === 0 ? (
          <p className="text-sm text-gray-500">No voices yet.</p>
        ) : (
          <ul className="space-y-2">
            {all.data.voices.map((v) => (
              <VoiceRow key={v.id} v={v} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function AddVoiceModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<CreateVoiceInput>({
    title: "",
    description: "",
    voiceType: "discussion",
    category: "cultural",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const valid =
    form.title.trim().length >= 5 && form.description.trim().length >= 10;

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await createVoice({
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
      });
      setForm({
        title: "",
        description: "",
        voiceType: "discussion",
        category: "cultural",
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to post");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Raise a voice"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} isLoading={busy} disabled={!valid}>
            Post
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
            {err}
          </div>
        )}
        <InputField
          label="Title"
          placeholder="At least 5 characters"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label="Type"
            value={form.voiceType}
            onChange={(e) =>
              setForm({ ...form, voiceType: e.target.value as CreateVoiceInput["voiceType"] })
            }
            options={VOICE_TYPES.map((t) => ({ value: t, label: t }))}
          />
          <SelectField
            label="Category"
            value={form.category}
            onChange={(e) =>
              setForm({ ...form, category: e.target.value as CreateVoiceInput["category"] })
            }
            options={VOICE_CATEGORIES.map((c) => ({
              value: c,
              label: c.replace(/_/g, " "),
            }))}
          />
        </div>
        <TextAreaField
          label="Details"
          rows={5}
          placeholder="At least 10 characters"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <MediaAttachPanel
          value={form.media ?? []}
          onChange={(media) => setForm({ ...form, media })}
        />
      </div>
    </Modal>
  );
}

/**
 * The carousel's write surface: attach your own uploads, in order, and
 * answer the origin screen per item. Two doors on purpose — the ordinary
 * upload, and the camera route whose attestation the server records because
 * of WHICH door the bytes used (the origin select disappears for those:
 * the attestation outranks any declaration). Dismissing the origin question
 * publishes — "no answer" is a real answer.
 */
function MediaAttachPanel({
  value,
  onChange,
}: {
  value: NonNullable<CreateVoiceInput["media"]>;
  onChange: (media: NonNullable<CreateVoiceInput["media"]>) => void;
}) {
  const [items, setItems] = useState<
    { uploadId: string; url: string; captureSource?: string }[]
  >([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const attach = async (file: File, viaCamera: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      const up = viaCamera ? await uploadCameraImage(file) : await uploadImage(file, "post");
      setItems((prev) => [...prev, { uploadId: up.id, url: up.url, captureSource: up.captureSource }]);
      onChange([...value, { uploadId: up.id }]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const setOrigin = (idx: number, patch: Partial<NonNullable<CreateVoiceInput["media"]>[number]>) => {
    onChange(value.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };

  const pick = (viaCamera: boolean) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const f = input.files?.[0];
      if (f) void attach(f, viaCamera);
    };
    input.click();
  };

  return (
    <div className="border-t border-gray-100 pt-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-600">Media</span>
        <Button variant="secondary" size="sm" onClick={() => pick(false)} disabled={busy}>
          Add image
        </Button>
        <Button variant="secondary" size="sm" onClick={() => pick(true)} disabled={busy}>
          Add via camera route
        </Button>
      </div>
      {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
      {items.map((it, idx) => {
        const m = value[idx];
        if (!m) return null;
        const cameraAttested = it.captureSource === "in_app_camera";
        const needsSource =
          m.declaredOrigin === "social_repost" || m.declaredOrigin === "news_clip";
        return (
          <div key={it.uploadId} className="mt-2 flex items-start gap-2 rounded-lg border border-gray-200 p-2">
            <img src={it.url} alt="" className="h-12 w-12 rounded object-cover" />
            <div className="flex-1 text-xs">
              {cameraAttested ? (
                <p className="text-emerald-700">
                  Camera-attested — the origin screen is skipped; the server
                  recorded the door the bytes came through.
                </p>
              ) : (
                <>
                  <label className="block text-gray-500">
                    Where did this come from?
                    <select
                      value={m.declaredOrigin ?? ""}
                      onChange={(e) =>
                        setOrigin(idx, {
                          declaredOrigin: (e.target.value ||
                            undefined) as NonNullable<CreateVoiceInput["media"]>[number]["declaredOrigin"],
                        })
                      }
                      className="mt-0.5 block w-full rounded border border-gray-200 px-2 py-1"
                    >
                      <option value="">No answer — publishes anyway</option>
                      {DECLARED_ORIGINS.map((o) => (
                        <option key={o} value={o}>
                          {o.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </label>
                  {needsSource && (
                    <input
                      value={m.sourceUrl ?? ""}
                      onChange={(e) => setOrigin(idx, { sourceUrl: e.target.value || undefined })}
                      placeholder="Source URL — required for reposts and news clips"
                      className="mt-1 block w-full rounded border border-gray-200 px-2 py-1"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VoiceRow({ v }: { v: { id: string; title: string; voiceType: string; intentType: string; user: { name: string } | null; city?: string | null; state?: string | null; createdAt: string } }) {
  return (
    <li>
      <Link
        to={`/lab/voices/${v.id}`}
        className="block bg-white border border-gray-200 rounded-xl px-4 py-3 hover:border-primary-300 hover:shadow-md transition-all"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900">{v.title}</span>
          <Badge variant="gray">{v.voiceType}</Badge>
          <Badge variant="purple">{v.intentType}</Badge>
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          {/* The author object is ABSENT, not blank, on an anonymous post —
              the server strips it rather than sending a placeholder name. */}
          by {v.user?.name ?? "Anonymous"}
          {v.state && ` · ${v.city ? `${v.city}, ` : ""}${v.state}`}
          {" · "}
          {new Date(v.createdAt).toLocaleDateString()}
        </div>
      </Link>
    </li>
  );
}

// ═══════════════════════════════════════════════════════════════════
// The pulse is the board's own vital signs for one week — and the only
// number on it that asks for anything is `helpPostsAwaitingResponse`:
// people who asked for help and got silence. Everything else describes
// the room; that one names a debt.
// ═══════════════════════════════════════════════════════════════════

function CommunityPulsePanel() {
  const [stateCode, setStateCode] = useState("");
  const { data, isLoading, error } = useQuery({
    queryKey: ["community-pulse", stateCode],
    queryFn: () => getCommunityPulse(stateCode ? { stateCode } : undefined),
  });

  const p = data?.data.pulse;

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h2 className="text-lg font-semibold text-gray-900">Community pulse</h2>
        <input
          value={stateCode}
          onChange={(e) => setStateCode(e.target.value.toUpperCase().slice(0, 2))}
          placeholder="State (CA)"
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-28"
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-700">
          {error instanceof Error ? error.message : "Could not read the pulse"}
        </p>
      ) : !p ? (
        <p className="text-sm text-gray-500">No pulse.</p>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="text-xs text-gray-500">
            Week of {p.weekOf}
            {p.stateCode ? ` · ${p.stateCode}` : " · everywhere"}
          </p>
          <div className="flex gap-5 flex-wrap">
            <div>
              <div className="text-xl font-semibold tabular-nums">{p.totalVoices}</div>
              <div className="text-xs text-gray-500">voices</div>
            </div>
            <div>
              <div className="text-xl font-semibold tabular-nums">
                {p.weekOverWeekChange > 0 ? "+" : ""}
                {p.weekOverWeekChange}%
              </div>
              <div className="text-xs text-gray-500">vs last week</div>
            </div>
            <div>
              <div
                className={`text-xl font-semibold tabular-nums ${
                  p.helpPostsAwaitingResponse > 0 ? "text-red-700" : "text-gray-900"
                }`}
              >
                {p.helpPostsAwaitingResponse}
              </div>
              <div className="text-xs text-gray-500">asked for help, unanswered</div>
            </div>
          </div>

          {p.topCategories.length > 0 && (
            <div>
              <p className="font-medium text-gray-900 mb-1">Top categories</p>
              <div className="flex gap-2 flex-wrap">
                {p.topCategories.map((c) => (
                  <Badge key={c.category} variant="blue">
                    {c.category.replace(/_/g, " ")}: {c.count}
                    {c.percentChange !== 0 && (
                      <span className="opacity-70">
                        {" "}
                        ({c.percentChange > 0 ? "+" : ""}
                        {c.percentChange}%)
                      </span>
                    )}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {Object.keys(p.intentBreakdown ?? {}).length > 0 && (
            <div>
              <p className="font-medium text-gray-900 mb-1">Why people spoke</p>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(p.intentBreakdown).map(([k, n]) => (
                  <Badge key={k} variant="gray">
                    {k.replace(/_/g, " ")}: {n}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// Browsing by type, which is a different question from browsing by category:
// a celebration and an issue want different responses even on one topic.

function ByType() {
  const [type, setType] = useState<string>(VOICE_TYPES[0]);
  const { data, isLoading } = useQuery({
    queryKey: ["voices-by-type", type],
    queryFn: () => listVoicesByType(type, { limit: 10 }),
  });

  const list = data?.data.voices ?? [];

  return (
    <section>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <h2 className="text-lg font-semibold text-gray-900">By type</h2>
        {VOICE_TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setType(t)}
            className={`px-2.5 py-1 rounded-full text-xs capitalize ${
              type === t ? "bg-gray-900 text-white" : "border border-gray-300 text-gray-600"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !list.length ? (
        <p className="text-sm text-gray-500">Nothing filed as {type}.</p>
      ) : (
        <ul className="space-y-2">
          {list.map((v) => (
            <VoiceRow key={v.id} v={v} />
          ))}
        </ul>
      )}
    </section>
  );
}
